// Brokerage Certification & Pipeline — staff-side clickable pipeline stepper.
// Modeled on the broker BrokerStageStepper: numbered 1–4 (Prospect → Applied →
// Under review → Certified); completed stages get checks, the current stage is
// highlighted, the NEXT stage carries a "Next" hint and is the emphasized click
// target. With Brokerages.Update the click targets are the immediate NEXT stage
// (forward is single-step) plus any EARLIER stage (a backward move can retreat
// to any prior stage); without the permission, or when the firm sits in an
// off-ramp / grandfathered-Active status, the stepper renders inert. Transitions
// are FULLY MANUAL — nothing here enforces the advisory certification checklist.

import { cn } from "@/design-system/lib/utils";
import {
  BROKERAGE_PIPELINE_STATUSES,
  isBrokerageArchiveStatus,
  type BrokerageStatus,
} from "@/api/brokerages";

// The only stage whose display label differs from its enum name.
const stageLabel = (s: BrokerageStatus) => (s === "UnderReview" ? "Under review" : s);

export interface BrokerageStageStepperProps {
  status: BrokerageStatus;
  /** Brokerages.Update — without it the stepper is inert. */
  canUpdate: boolean;
  onStageClick: (target: BrokerageStatus) => void;
}

export function BrokerageStageStepper({
  status,
  canUpdate,
  onStageClick,
}: BrokerageStageStepperProps) {
  const offPipeline = isBrokerageArchiveStatus(status) || status === "Active";
  const currentIndex = BROKERAGE_PIPELINE_STATUSES.indexOf(status); // -1 when off-pipeline
  const interactive = canUpdate && !offPipeline;

  return (
    <div className="flex flex-wrap items-center gap-space-2" data-testid="brokerage-stage-stepper">
      <ol
        className="flex flex-wrap items-center gap-space-2 list-none p-0 m-0"
        aria-label="Brokerage pipeline stage"
      >
        {BROKERAGE_PIPELINE_STATUSES.map((stage, idx) => {
          const isComplete = idx < currentIndex;
          const isActive = idx === currentIndex;
          const isNext = interactive && idx === currentIndex + 1;
          // Forward is single-step (only the immediate next stage); backward can
          // retreat to ANY earlier stage. The current stage is never its own target.
          const clickable = interactive && (idx === currentIndex + 1 || idx < currentIndex);

          const pill = (
            <span
              className={cn(
                "inline-flex items-center gap-space-1 rounded-pill border px-space-2 py-[3px] text-caption transition-colors duration-fast ease-standard",
                isActive && "border-gold bg-gold text-onyx font-semibold ring-1 ring-gold",
                isComplete && "border-status-success bg-status-success/15 text-status-success",
                !isActive && !isComplete && !isNext && "border-border-light bg-white-warm text-gray-mid",
                isNext && "border-gold bg-gold/10 text-onyx font-semibold",
                clickable && "cursor-pointer hover:border-gold-shadow",
              )}
              aria-current={isActive ? "step" : undefined}
              data-step={stage}
              data-state={isActive ? "active" : isComplete ? "complete" : "upcoming"}
            >
              <span className="inline-block w-4 text-center" aria-hidden="true">
                {isComplete ? "✓" : idx + 1}
              </span>
              {stageLabel(stage)}
              {isNext && (
                <span className="font-display text-label uppercase tracking-label text-gold-shadow">
                  Next
                </span>
              )}
            </span>
          );

          return (
            <li key={stage} className="flex items-center gap-space-2 shrink-0">
              {clickable ? (
                <button
                  type="button"
                  className="bg-transparent border-0 p-0 m-0"
                  onClick={() => onStageClick(stage)}
                  aria-label={`Move to ${stageLabel(stage)}`}
                  data-testid={`brokerage-stage-${stage.toLowerCase()}`}
                >
                  {pill}
                </button>
              ) : (
                <span data-testid={`brokerage-stage-${stage.toLowerCase()}`}>{pill}</span>
              )}
              {idx < BROKERAGE_PIPELINE_STATUSES.length - 1 && (
                <span className="text-caption text-gray-mid" aria-hidden="true">
                  →
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
